// src/components/pages/Placements.jsx
import React, { useState, useEffect } from 'react';
import storageService from '../../services/storageService';
import { APPLICATION_STATUS } from '../../constants/statusConstants';
import './Placements.css';

const Placements = () => {
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  const [formData, setFormData] = useState({
    jobId: '',
    candidateId: '',
    status: APPLICATION_STATUS.RESUME_SENT,
    appliedDate: new Date().toISOString().split('T')[0],
    notes: '',
    interviewer: '',
    interviewDate: '',
    interviewTime: ''
  });

  const [jobs, setJobs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [companies, setCompanies] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    const allPlacements = storageService.getPlacements();
    const allJobs = storageService.getJobs();
    const allCandidates = storageService.getCandidates();
    const allCompanies = storageService.getCompanies();
    
    const companiesMap = {};
    allCompanies.forEach(company => {
      companiesMap[company.id] = company;
    });
    
    const placementsWithDetails = allPlacements.map(placement => {
      const job = allJobs.find(j => j.id === placement.jobId);
      const candidate = allCandidates.find(c => c.id === placement.candidateId);
      const company = job ? companiesMap[job.companyId] : null;
      
      return {
        ...placement,
        jobTitle: job?.title,
        candidateName: candidate?.name,
        companyName: company?.name,
        candidateEmail: candidate?.email,
        candidatePhone: candidate?.phone,
        companyId: job?.companyId
      };
    }).filter(p => p.jobTitle);
    
    setPlacements(placementsWithDetails);
    setJobs(allJobs);
    setCandidates(allCandidates);
    setCompanies(companiesMap);
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    storageService.createPlacement(formData);
    
    resetForm();
    loadData();
    setShowModal(false);
  };

  const handleStatusChange = (placementId, newStatus) => {
    storageService.updatePlacement(placementId, { status: newStatus });
    loadData();
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this placement?')) {
      storageService.deletePlacement(id);
      loadData();
    }
  };

  const resetForm = () => {
    setFormData({
      jobId: '',
      candidateId: '',
      status: APPLICATION_STATUS.RESUME_SENT,
      appliedDate: new Date().toISOString().split('T')[0],
      notes: '',
      interviewer: '',
      interviewDate: '',
      interviewTime: ''
    });
  };

  // Filter placements
  const filteredPlacements = placements.filter(placement => {
    const matchesSearch = 
      placement.candidateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      placement.jobTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
      placement.companyName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || placement.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch(status) {
      case APPLICATION_STATUS.RESUME_SENT: return 'status-resume';
      case APPLICATION_STATUS.INTERVIEW_IN_PROGRESS: return 'status-interview';
      case APPLICATION_STATUS.SELECTED: return 'status-selected';
      case APPLICATION_STATUS.JOINED: return 'status-joined';
      case APPLICATION_STATUS.REJECTED: return 'status-rejected';
      default: return 'status-default';
    }
  };

  const getStatusStats = () => {
    const stats = {};
    placements.forEach(p => {
      stats[p.status] = (stats[p.status] || 0) + 1;
    });
    return stats;
  };

  const statusStats = getStatusStats();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading placements...</p>
      </div>
    );
  }

  return (
    <div className="placements-page">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1>Placements</h1>
          <p>Track candidate applications and placement status</p>
        </div>
        <button onClick={() => setShowModal(true)} className="btn-primary">
          <span className="btn-icon">+</span> New Application
        </button>
      </div>

      {/* Status Stats */}
      <div className="status-stats">
        {Object.values(APPLICATION_STATUS).map(status => (
          <div key={status} className="status-stat-card">
            <div className={`status-indicator ${getStatusColor(status)}`}></div>
            <div className="status-stat-content">
              <h3>{statusStats[status] || 0}</h3>
              <p>{status}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search placements by candidate, job, or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <span className="search-icon">🔍</span>
        </div>
        
        <div className="filter-controls">
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            {Object.values(APPLICATION_STATUS).map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Placements Table */}
      <div className="table-container">
        {filteredPlacements.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Job Position</th>
                <th>Company</th>
                <th>Applied Date</th>
                <th>Status</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPlacements.map(placement => (
                <tr key={placement.id}>
                  <td>
                    <div className="candidate-cell">
                      <div className="candidate-avatar">
                        {placement.candidateName.charAt(0)}
                      </div>
                      <div className="candidate-info">
                        <span className="candidate-name">{placement.candidateName}</span>
                        <div className="candidate-contact">
                          <span className="candidate-email">{placement.candidateEmail}</span>
                          <span className="candidate-phone">{placement.candidatePhone}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="job-cell">
                      <span className="job-title">{placement.jobTitle}</span>
                    </div>
                  </td>
                  <td>
                    <Link to={`/company/${placement.companyId}/jobs`} className="company-link">
                      {placement.companyName}
                    </Link>
                  </td>
                  <td>{placement.appliedDate}</td>
                  <td>
                    <select
                      value={placement.status}
                      onChange={(e) => handleStatusChange(placement.id, e.target.value)}
                      className={`status-select ${getStatusColor(placement.status)}`}
                    >
                      {Object.values(APPLICATION_STATUS).map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <div className="notes-cell">
                      {placement.notes || 'No notes'}
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <Link 
                        to={`/placement/${placement.id}/interview`}
                        className="action-btn schedule"
                        title="Schedule Interview"
                      >
                        📅
                      </Link>
                      <button 
                        onClick={() => handleDelete(placement.id)} 
                        className="action-btn delete"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h3>No placements found</h3>
            <p>Try creating a new application or adjust your search filters</p>
            <button onClick={() => setShowModal(true)} className="btn-primary">
              Create First Application
            </button>
          </div>
        )}
      </div>

      {/* Add Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Create New Application</h2>
              <button onClick={() => { resetForm(); setShowModal(false); }} className="modal-close">
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Select Job *</label>
                    <select
                      name="jobId"
                      value={formData.jobId}
                      onChange={handleInputChange}
                      required
                      className="form-select"
                    >
                      <option value="">Select Job</option>
                      {jobs.map(job => (
                        <option key={job.id} value={job.id}>
                          {job.title} - {companies[job.companyId]?.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Select Candidate *</label>
                    <select
                      name="candidateId"
                      value={formData.candidateId}
                      onChange={handleInputChange}
                      required
                      className="form-select"
                    >
                      <option value="">Select Candidate</option>
                      {candidates.map(candidate => (
                        <option key={candidate.id} value={candidate.id}>
                          {candidate.name} - {candidate.skills.split(',')[0]}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Status *</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      required
                      className="form-select"
                    >
                      {Object.values(APPLICATION_STATUS).map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Applied Date *</label>
                    <input
                      type="date"
                      name="appliedDate"
                      value={formData.appliedDate}
                      onChange={handleInputChange}
                      required
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Interviewer</label>
                    <input
                      type="text"
                      name="interviewer"
                      value={formData.interviewer}
                      onChange={handleInputChange}
                      placeholder="Interviewer name"
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Interview Date</label>
                    <input
                      type="date"
                      name="interviewDate"
                      value={formData.interviewDate}
                      onChange={handleInputChange}
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Interview Time</label>
                    <input
                      type="time"
                      name="interviewTime"
                      value={formData.interviewTime}
                      onChange={handleInputChange}
                      className="form-input"
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Add any notes about this application..."
                    className="form-textarea"
                  />
                </div>
              </div>
              
              <div className="modal-footer">
                <button type="submit" className="btn-primary">
                  Create Application
                </button>
                <button 
                  type="button" 
                  onClick={() => { resetForm(); setShowModal(false); }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper Link component
const Link = ({ to, className, children }) => (
  <a href={to} className={className}>{children}</a>
);

export default Placements;